import * as yup from "yup";

import { LoadingButton } from "@mui/lab";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Grid, Typography } from "@mui/material";
import AutoComplete from "@/components/hookForms/autoComplete";
import IGenericResult from "@/interfaces/IGenericResult";
import { useForm, useWatch } from "react-hook-form";
import serviceMarcador from "@/services/serviceMarcador";
import { toast } from "react-toastify";
import messages from "@/utils/messages";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";

export interface IProps {
    open: boolean;
    marcadorOptions: IGenericResult[];
    // Instalações selecionadas, com o marcador atual de cada uma (null quando não tem).
    instalacoes: { clienteInstalacaoID: number; marcadorID: number | null }[];
    onClose: any;
    onSuccess: any;
}

export default function DialogMarcador(props: IProps) {
    const [isLoading, setLoading] = useState(false);
    const quantidade = props.instalacoes.length;

    const validationSchema = yup.object().shape({
        marcadorID: yup.string().required(messages.requiredField),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            marcadorID: "",
        },
    });

    const marcadorSelecionado = useWatch({ control, name: "marcadorID" });

    // Cada instalação tem no máximo 1 marcador: as que já têm outro terão o marcador substituído.
    const quantidadeSubstituicao = marcadorSelecionado
        ? props.instalacoes.filter((i) => i.marcadorID && i.marcadorID !== Number(marcadorSelecionado)).length
        : 0;

    const onClose = () => {
        reset();
        props.onClose();
    };

    const onConfirm = (values: any) => {
        const payload = {
            marcadorID: Number(values.marcadorID),
            clienteInstalacaoIDs: props.instalacoes.map((i) => i.clienteInstalacaoID),
        };

        setLoading(true);
        serviceMarcador
            .atribuir(payload)
            .then((result) => {
                if (result) {
                    toast.success(result.data?.mensagem || messages.updateSucess);
                    props.onSuccess();
                }
            })
            .catch((err) => {
                toast.error(err?.response?.data?.mensagem || messages.updateError);
            })
            .finally(() => {
                setLoading(false);
                onClose();
            });
    };

    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            open={props.open}
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onClose();
                }
            }}
            aria-labelledby="dialog-marcador-title"
        >
            <DialogTitle id="dialog-marcador-title">Atribuir Marcador</DialogTitle>
            <form onSubmit={handleSubmit(onConfirm)}>
                <DialogContent dividers>
                    <Typography variant="body1">
                        {`Selecione o marcador que será atribuído a ${quantidade} instalação(ões) selecionada(s).`}
                    </Typography>
                    <Grid container>
                        <Grid item md={12} xs={12}>
                            <AutoComplete
                                id="marcadorID"
                                name="marcadorID"
                                label="Marcador"
                                options={props.marcadorOptions}
                                control={control}
                                error={errors}
                            />
                        </Grid>
                    </Grid>
                    {quantidadeSubstituicao > 0 && (
                        <Typography variant="body2" color="error">
                            {`Atenção: ${quantidadeSubstituicao} instalação(ões) já possui(em) outro marcador, que será substituído. Cada instalação pode ter apenas 1 marcador.`}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                    <Button
                        id="cancelar"
                        onClick={onClose}
                        color="warning"
                        variant="contained"
                        style={{ width: "159px", height: "44px" }}
                    >
                        Cancelar
                    </Button>
                    <LoadingButton
                        id="confirmar"
                        type="submit"
                        disabled={isLoading || quantidade <= 0}
                        loading={isLoading}
                        color="primary"
                        variant="contained"
                        style={{ width: "159px", height: "44px" }}
                    >
                        Confirmar
                    </LoadingButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}
